/**
 * useLinkHubState - Custom hook for Link Hub module state management
 * Extracted from Admin.jsx to reduce state complexity
 * 
 * No changes to behavior, API endpoints, or request payloads.
 */
import { useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../utils/apiUrl';

const getAuthHeaders = () => {
  const token = sessionStorage.getItem('golfnuts_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Theme presets for Link Hub pages
const THEME_PRESETS = {
  emerald_dark: {
    preset: 'emerald_dark',
    theme_mode: 'dark',
    background_type: 'gradient',
    gradient_start: '#0d3028',
    gradient_end: '#050f0c',
    card_background: 'rgba(15, 38, 32, 0.75)',
    accent_color: '#004534',
    accent_bright: '#00d99a',
    title_color: '#F9FAFB',
    bio_color: '#A3B3B3',
    link_title_color: '#F9FAFB',
    link_subtitle_color: '#9CA3AF',
    button_style: 'glass'
  },
  light_mode: {
    preset: 'light_mode',
    theme_mode: 'light',
    background_type: 'gradient',
    gradient_start: '#f0fdf9',
    gradient_end: '#ffffff',
    card_background: 'rgba(0, 69, 52, 0.08)',
    accent_color: '#004534',
    accent_bright: '#00d99a',
    title_color: '#111827',
    bio_color: '#6b7280',
    link_title_color: '#111827',
    link_subtitle_color: '#6b7280',
    button_style: 'glass'
  },
  neutral_gray: {
    preset: 'neutral_gray',
    theme_mode: 'dark',
    background_type: 'gradient',
    gradient_start: '#1f2937',
    gradient_end: '#111827',
    card_background: 'rgba(31, 41, 55, 0.8)',
    accent_color: '#6366f1',
    accent_bright: '#818cf8',
    title_color: '#F9FAFB',
    bio_color: '#9CA3AF',
    link_title_color: '#F9FAFB',
    link_subtitle_color: '#9CA3AF',
    button_style: 'glass'
  }
};

// Social platform field mapping
const SOCIAL_FIELD_MAPPING = {
  apple_podcasts_url: { platform: 'apple', name: 'Apple Podcasts' },
  spotify_url: { platform: 'spotify', name: 'Spotify' },
  youtube_url: { platform: 'youtube', name: 'YouTube' },
  instagram_url: { platform: 'instagram', name: 'Instagram' },
  x_url: { platform: 'twitter', name: 'X / Twitter' },
  tiktok_url: { platform: 'tiktok', name: 'TikTok' },
  threads_url: { platform: 'threads', name: 'Threads' },
  facebook_url: { platform: 'facebook', name: 'Facebook' },
  linkedin_url: { platform: 'linkedin', name: 'LinkedIn' },
  website_url: { platform: 'website', name: 'Website' },
  email_address: { platform: 'email', name: 'Email' }
};

const initialLinkHubForm = {
  title: 'New Link Hub',
  slug: '',
  published: true,
  header: {
    display_name: 'New Link Hub',
    bio: 'Add your bio here',
    avatar_image: ''
  },
  theme: {
    theme_mode: 'auto',
    accent_color: '#004534',
    background_style: 'gradient',
    button_style: 'solid'
  },
  links: [],
  social_accounts: [],
  default_social_toggles: {},
  blocks: [],
  isNew: true
};

export const useLinkHubState = ({ setError, setSuccess }) => {
  // Core Link Hub state
  const [linkHubPages, setLinkHubPages] = useState([]);
  const [loadingLinkHub, setLoadingLinkHub] = useState(false);
  const [editingLinkHub, setEditingLinkHub] = useState(null);
  const [showLinkHubModal, setShowLinkHubModal] = useState(false);
  const [linkHubModalTab, setLinkHubModalTab] = useState('content'); // 'content' | 'design'
  const [linkHubModalView, setLinkHubModalView] = useState('edit'); // 'edit' | 'media'
  const [globalSocialLinks, setGlobalSocialLinks] = useState([]);

  // Fetch all Link Hub pages
  const fetchLinkHubPages = useCallback(async () => {
    setLoadingLinkHub(true);
    try {
      const response = await axios.get(`${API_URL}/api/link-hub/pages`);
      setLinkHubPages(response.data || []);
    } catch (err) {
      console.error('Error fetching Link Hub pages:', err);
      setError('Failed to load Link Hub pages');
    } finally {
      setLoadingLinkHub(false);
    }
  }, [setError]);

  // Fetch global social links for Link Hub editor
  const fetchGlobalSocialLinks = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/social-links/`, {
        params: { profile_key: 'default' }
      });
      
      const data = response.data;
      const formattedLinks = [];
      
      for (const [field, info] of Object.entries(SOCIAL_FIELD_MAPPING)) {
        const url = data[field];
        if (url && url.trim() !== '' && url !== '#') {
          formattedLinks.push({
            id: `default-${info.platform}`,
            platform: info.platform,
            name: info.name,
            url: url,
            isDefault: true
          });
        }
      }
      
      setGlobalSocialLinks(formattedLinks);
    } catch (err) {
      console.error('Error fetching global social links:', err);
    }
  }, []);

  // Create new Link Hub page
  const handleCreateLinkHub = useCallback(() => {
    const newPage = {
      ...initialLinkHubForm,
      slug: `link-hub-${Date.now()}`
    };
    fetchGlobalSocialLinks();
    setEditingLinkHub(newPage);
    setShowLinkHubModal(true);
  }, [fetchGlobalSocialLinks]);

  // Edit existing Link Hub page
  const handleEditLinkHub = useCallback((page) => {
    fetchGlobalSocialLinks();
    setEditingLinkHub({ ...page });
    setShowLinkHubModal(true);
  }, [fetchGlobalSocialLinks]);

  // Save Link Hub page (create or update)
  const handleSaveLinkHub = useCallback(async () => {
    if (!editingLinkHub) return;
    
    if (!editingLinkHub.title?.trim()) {
      setError('Link Hub title is required');
      return;
    }
    
    if (!editingLinkHub.slug?.trim()) {
      setError('Link Hub slug is required');
      return;
    }
    
    try {
      if (editingLinkHub.isNew) {
        const { isNew, ...pageData } = editingLinkHub;
        await axios.post(`${API_URL}/api/link-hub/pages`, pageData, {
          headers: getAuthHeaders()
        });
        setSuccess(`"${editingLinkHub.title}" created successfully!`);
      } else {
        await axios.put(`${API_URL}/api/link-hub/pages/${editingLinkHub.id}`, editingLinkHub, {
          headers: getAuthHeaders()
        });
        setSuccess(`"${editingLinkHub.title}" saved successfully!`);
      }
      setShowLinkHubModal(false);
      setEditingLinkHub(null);
      fetchLinkHubPages();
    } catch (err) {
      setError('Failed to save Link Hub: ' + (err.response?.data?.detail || err.message));
    }
  }, [editingLinkHub, fetchLinkHubPages, setError, setSuccess]);

  // Delete Link Hub page
  const handleDeleteLinkHub = useCallback(async (pageId, pageTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${pageTitle}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/api/link-hub/pages/${pageId}`, {
        headers: getAuthHeaders()
      });
      setSuccess(`"${pageTitle}" deleted successfully`);
      fetchLinkHubPages();
    } catch (err) {
      setError('Failed to delete Link Hub: ' + (err.response?.data?.detail || err.message));
    }
  }, [fetchLinkHubPages, setError, setSuccess]);

  // Handle field changes in edit form
  const handleLinkHubFieldChange = useCallback((field, value) => {
    setEditingLinkHub(prev => prev ? ({ ...prev, [field]: value }) : null);
  }, []);

  // ============================================
  // LINK MANAGEMENT FUNCTIONS
  // ============================================

  const handleAddLink = useCallback(() => {
    const newLink = {
      id: `link-${Date.now()}`,
      label: '',
      url: '',
      subtext: '',
      featured: false,
      enabled: true,
      coming_soon: false
    };
    setEditingLinkHub(prev => prev ? ({
      ...prev,
      links: [...(prev.links || []), newLink]
    }) : null);
  }, []);

  const handleUpdateLink = useCallback((linkId, field, value) => {
    setEditingLinkHub(prev => prev ? ({
      ...prev,
      links: prev.links.map(link => 
        link.id === linkId ? { ...link, [field]: value } : link
      )
    }) : null);
  }, []);

  const handleRemoveLink = useCallback((linkId) => {
    setEditingLinkHub(prev => prev ? ({
      ...prev,
      links: prev.links.filter(link => link.id !== linkId)
    }) : null);
  }, []);

  const handleMoveLink = useCallback((linkId, direction) => {
    setEditingLinkHub(prev => {
      if (!prev) return null;
      const links = [...prev.links];
      const index = links.findIndex(l => l.id === linkId);
      if (direction === 'up' && index > 0) {
        [links[index - 1], links[index]] = [links[index], links[index - 1]];
      } else if (direction === 'down' && index < links.length - 1) {
        [links[index], links[index + 1]] = [links[index + 1], links[index]];
      }
      return { ...prev, links };
    });
  }, []);

  // ============================================
  // SOCIAL LINKS MANAGEMENT FUNCTIONS
  // ============================================

  const handleAddSocialLink = useCallback(() => {
    const newSocial = {
      id: `social-${Date.now()}`,
      platform: 'instagram',
      url: '',
      enabled: true
    };
    setEditingLinkHub(prev => prev ? ({
      ...prev,
      social_links: [...(prev.social_links || []), newSocial]
    }) : null);
  }, []);

  const handleUpdateSocialLink = useCallback((socialId, field, value) => {
    setEditingLinkHub(prev => prev ? ({
      ...prev,
      social_links: (prev.social_links || []).map(social => 
        social.id === socialId ? { ...social, [field]: value } : social
      )
    }) : null);
  }, []);

  const handleRemoveSocialLink = useCallback((socialId) => {
    setEditingLinkHub(prev => prev ? ({
      ...prev,
      social_links: (prev.social_links || []).filter(social => social.id !== socialId)
    }) : null);
  }, []);

  // Toggle default social link visibility for this Link Hub
  const handleToggleDefaultSocial = useCallback((platform) => {
    setEditingLinkHub(prev => {
      if (!prev) return null;
      const currentToggles = prev.default_social_toggles || {};
      const currentValue = currentToggles[platform] !== false;
      return {
        ...prev,
        default_social_toggles: {
          ...currentToggles,
          [platform]: !currentValue
        }
      };
    });
  }, []);

  // Check if a default social link is enabled
  const isDefaultSocialEnabled = useCallback((platform) => {
    if (!editingLinkHub) return true;
    const toggles = editingLinkHub.default_social_toggles || {};
    return toggles[platform] !== false;
  }, [editingLinkHub]);

  // ============================================
  // THEME MANAGEMENT FUNCTIONS
  // ============================================

  const handleLinkHubThemeChange = useCallback((field, value) => {
    setEditingLinkHub(prev => prev ? ({
      ...prev,
      theme: {
        ...prev.theme,
        [field]: value
      }
    }) : null);
  }, []);

  const applyThemePreset = useCallback((presetName) => {
    if (THEME_PRESETS[presetName]) {
      setEditingLinkHub(prev => prev ? ({
        ...prev,
        theme: { ...prev.theme, ...THEME_PRESETS[presetName] }
      }) : null);
    }
  }, []);

  return {
    // State
    linkHubPages,
    loadingLinkHub,
    editingLinkHub,
    showLinkHubModal,
    linkHubModalTab,
    linkHubModalView,
    globalSocialLinks,

    // Setters
    setLinkHubPages,
    setEditingLinkHub,
    setShowLinkHubModal,
    setLinkHubModalTab,
    setLinkHubModalView,
    setGlobalSocialLinks,

    // Core actions
    fetchLinkHubPages,
    fetchGlobalSocialLinks,
    handleCreateLinkHub,
    handleEditLinkHub,
    handleSaveLinkHub,
    handleDeleteLinkHub,
    handleLinkHubFieldChange,

    // Link management
    handleAddLink,
    handleUpdateLink,
    handleRemoveLink,
    handleMoveLink,

    // Social link management
    handleAddSocialLink,
    handleUpdateSocialLink,
    handleRemoveSocialLink,
    handleToggleDefaultSocial,
    isDefaultSocialEnabled,

    // Theme management
    handleLinkHubThemeChange,
    applyThemePreset,
  };
};

export default useLinkHubState;
